import type { ConversionEmailProps } from "./types";

const FONT = "Arial, Helvetica, sans-serif";

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function metricTile(value: string | number, label: string, color: string, valueSize = 28): string {
  return `
  <td valign="top" width="33%" align="center" style="padding:6px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#1F3864;border-radius:8px;">
      <tr><td align="center" style="padding:16px 8px;">
        <div style="font-family:${FONT};color:${color};font-size:${valueSize}px;font-weight:bold;line-height:${valueSize + 4}px;">${esc(value)}</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:18px;margin-top:6px;">${esc(label)}</div>
      </td></tr>
    </table>
  </td>`;
}

function planBullet(text: string): string {
  return `
  <tr>
    <td width="16" valign="top" style="font-family:${FONT};color:#16A34A;font-size:14px;line-height:22px;">&#10003;</td>
    <td valign="top" style="font-family:${FONT};color:#374151;font-size:13px;line-height:20px;padding:0 0 6px 6px;">${esc(text)}</td>
  </tr>`;
}

function consequenceRow(text: string, last: boolean): string {
  return `
  <tr>
    <td width="20" valign="top" style="font-family:${FONT};color:#DC2626;font-size:14px;font-weight:bold;line-height:22px;padding:10px 0;${last ? "" : "border-bottom:1px solid #FECACA;"}">&#10007;</td>
    <td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:22px;padding:10px 0 10px 8px;${last ? "" : "border-bottom:1px solid #FECACA;"}">${esc(text)}</td>
  </tr>`;
}

function planCard(opts: {
  name: string;
  price: string;
  tagline: string;
  features: string[];
  ctaLabel: string;
  ctaUrl: string;
  recommended: boolean;
}): string {
  const border = opts.recommended ? "#D4A843" : "#E5E7EB";
  const badge = opts.recommended
    ? `<div style="background-color:#D4A843;color:#1F3864;font-family:${FONT};font-size:10px;font-weight:bold;letter-spacing:1px;padding:3px 10px;border-radius:4px;text-align:center;display:block;margin-bottom:12px;">RECOMMENDED</div>`
    : "";
  const cta = opts.recommended
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="background-color:#D4A843;border-radius:6px;"><a href="${esc(opts.ctaUrl)}" style="display:block;padding:12px 16px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;text-align:center;">${esc(opts.ctaLabel)}</a></td></tr></table>`
    : `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="background-color:#FFFFFF;border:2px solid #1F3864;border-radius:6px;"><a href="${esc(opts.ctaUrl)}" style="display:block;padding:10px 14px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;text-align:center;">${esc(opts.ctaLabel)}</a></td></tr></table>`;

  return `
  <td valign="top" width="50%" style="padding:6px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFFFFF;border:2px solid ${border};border-radius:10px;">
      <tr><td style="padding:20px;">
        ${badge}
        <div style="font-family:${FONT};color:#1F3864;font-size:18px;font-weight:bold;line-height:24px;">${esc(opts.name)}</div>
        <div style="font-family:${FONT};line-height:40px;margin-top:4px;">
          <span style="color:#D4A843;font-size:32px;font-weight:bold;">${esc(opts.price)}</span>
          <span style="color:#6B7280;font-size:13px;"> / month</span>
        </div>
        <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:18px;margin-top:4px;">${esc(opts.tagline)}</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:14px 0 16px;">
          ${opts.features.map(planBullet).join("")}
        </table>
        ${cta}
      </td></tr>
    </table>
  </td>`;
}

export function generateConversionEmailHTML(p: ConversionEmailProps): string {
  const isStarter = p.currentPlan === "Starter";
  const isGroup = p.segment === "grouphome";

  const headline = isStarter
    ? `Don&#39;t lose your ${esc(p.segment_label)}.`
    : `You built something real, ${esc(p.ownerName)}.`;
  const subhead = isStarter
    ? "When your trial ends, your chatbot goes offline and your leads stop being captured. Upgrade to keep going."
    : "Upgrade now and keep everything you&#39;ve set up &mdash; your scoring engine, your templates, your referral contacts.";

  const tile1Label = isGroup ? "Inquiries Received" : "Leads Captured";
  const tile2Label = isGroup ? "Urgent Inquiries (85+)" : "Hot Leads (85+)";

  const starterCard = planCard({
    name: "Starter",
    price: p.starterPlanPrice,
    tagline: "For solo operators getting started",
    features: [
      "AI Lead Capture (Sarah chatbot)",
      "Lead Scoring Engine",
      "Up to 50 leads/month",
      "5 Email Templates",
      "Email Notifications",
    ],
    ctaLabel: isStarter ? "Choose Starter \u2192" : "Choose Starter \u2192",
    ctaUrl: p.upgradeUrl,
    recommended: false,
  });

  const growthCard = planCard({
    name: "Growth",
    price: p.growthPlanPrice,
    tagline: "For agencies ready to scale",
    features: [
      "Everything in Starter",
      "Unlimited leads",
      "All 14 email templates",
      "Outbound Referral Generator",
      "Hot Lead SMS Alerts + Emergency Alerts",
    ],
    ctaLabel: "Upgrade to Growth \u2192",
    ctaUrl: p.upgradeUrl,
    recommended: isStarter,
  });

  const planCardsRow = isStarter
    ? `<tr>${starterCard}${growthCard}</tr>`
    : `<tr>${growthCard}${planCard({
        name: "Agency Pro",
        price: "$397",
        tagline: "For multi-location agencies",
        features: [
          "Everything in Growth",
          "Unlimited team seats",
          "Multi-location support",
          "Priority support",
          "Custom integrations",
        ],
        ctaLabel: "Upgrade to Agency Pro \u2192",
        ctaUrl: p.upgradeUrl,
        recommended: true,
      })}</tr>`;

  const consequence2 = isGroup
    ? `Your ${p.totalLeadsCaptured} captured inquiries will become read-only — you cannot update statuses or send outreach emails.`
    : `Your ${p.totalLeadsCaptured} captured leads will become read-only — you cannot update statuses or send outreach emails.`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Your trial ends in 3 days</title>
</head>
<body style="margin:0;padding:0;background-color:#F0F2F5;font-family:${FONT};">
<div style="display:none;max-height:0;overflow:hidden;color:transparent;visibility:hidden;mso-hide:all;font-size:1px;line-height:1px;">You have captured ${esc(p.totalLeadsCaptured)} ${esc(p.segment_label)} during your trial. Keep the momentum going.</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0F2F5;">
  <tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;">

      <tr><td style="background-color:#0F1E35;padding:32px 40px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td valign="middle" style="font-family:${FONT};color:#D4A843;font-size:18px;font-weight:bold;line-height:24px;">LegacyGrowth Engine</td>
            <td valign="middle" align="right">
              <span style="background-color:#1F3864;color:#D4A843;font-family:${FONT};font-size:11px;font-weight:bold;padding:4px 10px;border-radius:4px;display:inline-block;">${esc(p.currentPlan)} Plan &mdash; Free Trial</span>
            </td>
          </tr>
        </table>
        <div style="border-top:1px solid #2E3D55;margin:18px 0 16px;"></div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td align="center" style="background-color:#DC2626;border-radius:8px;padding:16px 28px;">
          <div style="font-family:${FONT};color:#FFFFFF;font-size:14px;line-height:18px;text-align:center;">&#9200;</div>
          <div style="font-family:${FONT};color:#FFFFFF;font-size:28px;font-weight:bold;line-height:36px;text-align:center;">3 DAYS LEFT</div>
          <div style="font-family:${FONT};color:#FECACA;font-size:14px;line-height:20px;text-align:center;margin-top:4px;">Your free trial expires on ${esc(p.trialEndDate)}</div>
        </td></tr></table>
        <div style="font-family:${FONT};color:#FFFFFF;font-size:24px;font-weight:bold;line-height:30px;text-align:center;margin-top:16px;">${headline}</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:15px;line-height:24px;text-align:center;margin-top:8px;">${subhead}</div>
      </td></tr>

      <tr><td style="background-color:#FFFFFF;padding:36px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">What you accomplished during your trial</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#0F1E35;border-radius:10px;">
          <tr><td style="padding:24px;">
            <div style="font-family:${FONT};color:#9AAFC8;font-size:11px;font-weight:bold;letter-spacing:1.5px;line-height:16px;text-align:center;margin-bottom:16px;">YOUR 14-DAY TRIAL REPORT</div>
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                ${metricTile(p.totalLeadsCaptured, tile1Label, "#D4A843")}
                ${metricTile(p.hotLeadsTotal, tile2Label, "#EF4444")}
                ${metricTile(p.callsBooked, "Calls Booked", "#22C55E")}
              </tr>
              <tr>
                ${metricTile(p.estimatedPipelineValue, "Estimated Pipeline Value", "#D4A843", 22)}
                ${metricTile(p.teamMembersAdded, "Team Members Added", "#60A5FA")}
                ${metricTile(p.referralEmailsSent, "Referral Emails Sent", "#A78BFA")}
              </tr>
            </table>
            ${
              p.totalLeadsCaptured > 0
                ? `<div style="border-top:1px solid #2E3D55;margin:20px 0 16px;"></div>
                   <div style="font-family:${FONT};color:#9AAFC8;font-size:14px;line-height:20px;text-align:center;">If just one of your ${esc(p.totalLeadsCaptured)} ${esc(p.segment_label)} converts to a client, that is</div>
                   <div style="font-family:${FONT};color:#D4A843;font-size:20px;font-weight:bold;line-height:26px;text-align:center;margin-top:6px;">~${esc(p.estimatedPipelineValue)} in monthly revenue${isGroup ? " potential" : ""}</div>
                   <div style="font-family:${FONT};color:#9AAFC8;font-size:14px;line-height:20px;text-align:center;margin-top:4px;">Don&#39;t let that go.</div>`
                : ""
            }
          </td></tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#F8F9FB;border-top:1px solid #E5E7EB;padding:32px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">Choose your plan</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${planCardsRow}</table>
        <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:20px;text-align:center;margin-top:16px;">No long-term contracts. Cancel anytime. Setup takes under 30 minutes.</div>
      </td></tr>

      <tr><td style="background-color:#FEF2F2;border-top:3px solid #DC2626;padding:24px 40px;">
        <div style="font-family:${FONT};color:#991B1B;font-size:15px;font-weight:bold;line-height:22px;margin-bottom:12px;">If you don&#39;t upgrade by ${esc(p.trialEndDate)}...</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          ${consequenceRow("Your AI chatbot goes offline — new leads can no longer be captured from your website.", false)}
          ${consequenceRow(consequence2, false)}
          ${consequenceRow("Your lead scoring engine pauses — no more hot lead or emergency SMS alerts.", false)}
          ${consequenceRow("Your referral email templates become inaccessible — outbound outreach stops.", true)}
        </table>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0FDF4;border:1px solid #BBF7D0;border-radius:6px;margin-top:16px;">
          <tr><td style="padding:14px 16px;font-family:${FONT};color:#374151;font-size:13px;line-height:20px;">
            <span style="color:#16A34A;font-weight:bold;">&#10003; </span>Everything you&#39;ve set up &mdash; your lead filters, Golden Zones, specialties, and team &mdash; is saved. Upgrade anytime within 30 days and it all comes back instantly.
          </td></tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#FFFFFF;padding:28px 40px 36px;" align="center">
        <div style="font-family:${FONT};color:#1F3864;font-size:20px;font-weight:bold;line-height:26px;text-align:center;">Lock in your plan today.</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:22px;text-align:center;margin-top:8px;">Upgrade takes 2 minutes. No new setup required &mdash; everything you&#39;ve built during your trial carries over.</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:20px auto 0;">
          <tr><td align="center" style="background-color:#D4A843;border-radius:6px;">
            <a href="${esc(p.upgradeUrl)}" style="display:inline-block;padding:14px 28px;font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;text-decoration:none;line-height:20px;">Upgrade My Account &rarr;</a>
          </td></tr>
        </table>
        <div style="font-family:${FONT};color:#2E75B6;font-size:13px;line-height:20px;text-align:center;margin-top:18px;">Have questions? Reply to this email or contact <a href="mailto:${esc(p.supportEmail)}" style="color:#2E75B6;text-decoration:underline;">${esc(p.supportEmail)}</a></div>
      </td></tr>

      <tr><td style="background-color:#0F1E35;padding:24px 40px;">
        <div style="font-family:${FONT};line-height:20px;">
          <span style="color:#D4A843;font-size:14px;font-weight:bold;">LegacyGrowth Engine</span><span style="color:#9AAFC8;font-size:14px;"> &mdash; A product of H&amp;E Associates LLC</span>
        </div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:20px;margin-top:6px;">McLean, Virginia &nbsp;&middot;&nbsp; ${esc(p.supportEmail)}</div>
        <div style="margin-top:10px;font-family:${FONT};font-size:12px;line-height:18px;">
          <a href="mailto:${esc(p.supportEmail)}" style="color:#D4A843;text-decoration:underline;">Contact Support</a>
          <span style="color:#9AAFC8;"> &middot; </span>
          <a href="${esc(p.unsubscribeUrl)}" style="color:#9AAFC8;text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="font-family:${FONT};color:#4B5563;font-size:11px;line-height:18px;margin-top:12px;">You are receiving this email because your LegacyGrowth Engine free trial expires on ${esc(p.trialEndDate)}. This is a transactional account notification. To manage billing, visit your account settings at ${esc(p.upgradeUrl)}.</div>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}
